import { ErrorGroupsResult, TxSearchResult, type TxSummary } from '@apm/contracts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toQuery } from '../api';
import { useQuery } from '../hooks/useQuery';
import { TX_TYPE_TONE } from '../live/format';
import { currentQuery, navigate, replaceQuery, routeHref } from '../router';
import { useServiceFilter } from '../services/service-filter';
import { statusLevel, statusText } from '../status';
import { useLiveSelector } from '../store/useStream';
import { TIP, TX_TYPE_LABEL } from '../terms';
import { formatCount, formatDuration, formatFull, formatStamp } from '../time';
import {
  Button,
  Cell,
  Chip,
  type Column,
  EmptyState,
  ErrorText,
  Field,
  LimitNote,
  Lines,
  Loading,
  Page,
  type Range,
  RangePicker,
  Row,
  rangeMs,
  rangeParams,
  Section,
  Select,
  Stat,
  Table,
  Toggle,
} from '../ui';
import { ErrorGroups } from './ErrorGroups';

/**
 * 화면 — 오류 (19회차, 22-B회차 묶기).
 *
 * 16회차에 요약 행이 `errorMessage`·`instance`를 싣기 시작했고 서버가 인스턴스별 오류 창을
 * 세기 시작했는데, 콘솔에는 그것을 한자리에서 보는 화면이 없었다. 실시간 카드의 배지와
 * X-View의 빨간 점을 눈으로 좇는 것이 전부였다.
 *
 * 22-B회차에 **같은 오류 묶기**가 기본이 됐다(빠진 정보 3). 같은 404가 200줄 이어지면 그 사이의
 * 다른 오류가 묻힌다. 원인별로 묶어 건수와 처음·마지막을 보이고, 펼치면 개별 요청이 나온다.
 * 조회는 `GET /api/stats/errors`고, 묶지 않는 목록은 지금까지처럼 `GET /api/tx/search`다.
 */

const PAGE_SIZE = 200;
const GROUP_LIMIT = 200;

const COLUMNS: readonly Column[] = [
  { label: '시각', width: 'w-40' },
  { label: '심각도', width: 'w-28' },
  { label: '유형', width: 'w-28' },
  { label: '요청 경로', title: TIP.entry },
  { label: '응답시간', width: 'w-28' },
  { label: '원인' },
  { label: '인스턴스', width: 'w-44', title: TIP.instance },
];

type Severity = 'all' | 'error' | 'fail';

export function ErrorsPage({
  query,
  range,
  onRangeChange,
}: {
  query?: string;
  /** 진단 하위 탭이 함께 쓰는 기간 (22-B회차). 진단 메뉴가 들고 있다 */
  range: Range;
  onRangeChange: (next: Range) => void;
}) {
  // 인스턴스 목록만 본다 (D-C1)
  const liveInstances = useLiveSelector((live) => live.instances);
  const service = useServiceFilter();
  const serviceParam = service === '' ? undefined : service;
  const initial = useMemo(() => parseQuery(query), [query]);
  const [severity, setSeverity] = useState<Severity>(initial.severity);
  const [instance, setInstance] = useState(initial.instance);
  const [grouped, setGrouped] = useState(initial.grouped);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  // 서비스에 종속된 값만 비운다. 기간·심각도·묶음 여부는 화면을 다시 만들지 않고 유지한다.
  const previousService = useRef(service);
  useEffect(() => {
    if (previousService.current === service) return;
    previousService.current = service;
    setInstance('');
  }, [service]);

  const bounds = useMemo(() => rangeMs(range), [range]);
  const flatUrl = `/api/tx/search${toQuery({
    from: bounds.from,
    to: bounds.to,
    // 심각도를 안 고르면 서버에 조건을 걸지 않고 화면에서 오류·실패만 남긴다.
    // 서버 검색의 `status`는 한 단계만 받는다
    status: severity === 'all' ? undefined : severity,
    instance: instance === '' ? undefined : instance,
    service: serviceParam,
    limit: PAGE_SIZE,
  })}`;
  const groupsUrl = `/api/stats/errors${toQuery({
    from: bounds.from,
    to: bounds.to,
    level: severity === 'all' ? undefined : severity,
    instance: instance === '' ? undefined : instance,
    service: serviceParam,
    limit: GROUP_LIMIT,
  })}`;
  // 둘 중 지금 보는 쪽만 부른다
  const flat = useQuery(grouped ? undefined : flatUrl, TxSearchResult);
  const groups = useQuery(grouped ? groupsUrl : undefined, ErrorGroupsResult);
  const loading = grouped ? groups.loading : flat.loading;
  const error = grouped ? groups.error : flat.error;
  const reload = grouped ? groups.reload : flat.reload;
  const data = flat.data;

  // 조건을 주소에 남긴다 (후보 #8과 같은 길)
  useEffect(() => {
    const params = rangeParams(range);
    if (severity !== 'all') params.set('severity', severity);
    if (instance !== '') params.set('instance', instance);
    if (!grouped) params.set('grouped', '0');
    replaceQuery({ name: 'diagnose', sub: 'errors', query: params.toString() });
  }, [range, severity, instance, grouped]);

  // 전체를 고른 경우 정상 요청이 섞여 오므로 화면에서 한 번 더 거른다
  const rows = useMemo(() => (data?.rows ?? []).filter((row) => statusLevel(row) !== 'ok'), [data]);

  const instances = useMemo(() => {
    const names = new Set<string>();
    for (const snapshot of liveInstances) {
      if (service === '' || snapshot.service === service) names.add(snapshot.instance);
    }
    for (const row of data?.rows ?? []) if (row.instance !== undefined) names.add(row.instance);
    return [...names].sort();
  }, [liveInstances, data, service]);

  const counts = useMemo(() => {
    if (grouped) {
      const result = groups.data;
      if (result === undefined) return { errors: 0, fails: 0, total: 0, days: undefined };
      return {
        errors: result.errorCount,
        fails: result.totalCount - result.errorCount,
        total: result.totalCount,
        days: result.days,
      };
    }
    let errors = 0;
    let fails = 0;
    for (const row of rows) {
      if (statusLevel(row) === 'error') errors += 1;
      else fails += 1;
    }
    return { errors, fails, total: data?.total ?? 0, days: data?.days };
  }, [grouped, groups.data, rows, data]);

  const toggle = (txId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId);
      else next.add(txId);
      return next;
    });
  };

  const empty = grouped
    ? groups.data !== undefined && groups.data.rows.length === 0
    : data !== undefined && rows.length === 0;
  const truncated = grouped ? groups.data?.truncated === true : data?.truncated === true;

  return (
    <Page>
      <Section
        boxed
        title="오류"
        note={
          grouped
            ? [
                '정상으로 끝나지 않은 요청을 같은 원인끼리 묶는다.',
                '펼치면 개별 요청이 나오고, 시각을 누르면 상세로 간다',
              ]
            : [
                '정상으로 끝나지 않은 요청만 모아 본다.',
                '행을 누르면 상세로 가고, 예외 메시지는 자세히로 펼친다',
              ]
        }
        badge={
          grouped && groups.data !== undefined
            ? `원인 ${formatCount(groups.data.total)}가지`
            : undefined
        }
        actions={
          <Button size="sm" onClick={reload} disabled={loading}>
            {loading ? '읽는 중…' : '새로고침'}
          </Button>
        }
      >
        <div className="mb-4 flex flex-wrap items-end gap-4">
          <RangePicker idPrefix="errors" value={range} onChange={onRangeChange} />
          <div className="flex flex-wrap items-end gap-4">
            <Field label="심각도" htmlFor="errors-severity">
              <Select
                id="errors-severity"
                value={severity}
                onChange={(value) => setSeverity(value as Severity)}
                options={[
                  { value: 'all', label: '전체' },
                  { value: 'error', label: '오류만 (5xx·예외)' },
                  { value: 'fail', label: '실패만 (4xx·DB 오류)' },
                ]}
              />
            </Field>
            <Field label="인스턴스" htmlFor="errors-instance" hint={TIP.instance}>
              <Select
                id="errors-instance"
                value={instance}
                onChange={setInstance}
                options={[
                  { value: '', label: '전체' },
                  ...instances.map((name) => ({ value: name, label: name })),
                ]}
              />
            </Field>
            <div className="pb-2">
              <Toggle
                id="errors-grouped"
                checked={grouped}
                onChange={setGrouped}
                label="같은 오류 묶기"
                hint="같은 원인(예외 종류·상태 코드·DB 오류 코드·경로)끼리 한 줄로. 끄면 시각순 전체 목록이다"
              />
            </div>
          </div>
        </div>

        <dl className="mb-4 grid gap-3 sm:grid-cols-3">
          <Stat
            label="오류 (5xx·예외)"
            value={`${formatCount(counts.errors)}건`}
            level={counts.errors > 0 ? 'danger' : 'normal'}
            warnLabel="확인 필요"
          />
          <Stat
            label="실패 (4xx·DB 오류)"
            value={`${formatCount(counts.fails)}건`}
            level={counts.fails > 0 ? 'warn' : 'normal'}
            warnLabel="확인"
          />
          <Stat
            label={grouped ? '오류·실패 요청' : '조회한 요청'}
            value={`${formatCount(counts.total)}건`}
            note={counts.days !== undefined ? `날짜 폴더 ${counts.days}개 읽음` : undefined}
          />
        </dl>

        {error !== undefined ? <ErrorText>{error}</ErrorText> : null}
        {loading && (grouped ? groups.data : data) === undefined ? <Loading /> : null}
        {truncated ? (
          <p className="mb-3 whitespace-pre-line text-xs text-fail">{TIP.truncated}</p>
        ) : null}

        {empty ? (
          <EmptyState title="이 기간에 오류·실패가 없다" hint="기간을 넓혀 볼 수 있다" />
        ) : null}

        {grouped && groups.data !== undefined ? (
          <>
            <ErrorGroups
              groups={groups.data.rows}
              range={bounds}
              instance={instance === '' ? undefined : instance}
              service={serviceParam}
              showService={service === ''}
            />
            <LimitNote
              shown={groups.data.rows.length}
              total={groups.data.total}
              unit="가지"
              hint="기간을 좁히거나 심각도·인스턴스를 골라 볼 것"
            />
          </>
        ) : null}

        {!grouped && rows.length > 0 ? (
          <Table
            columns={service === '' ? [{ label: '서비스', width: 'w-40' }, ...COLUMNS] : COLUMNS}
            caption="오류·실패 요청"
            minWidth="min-w-[1100px]"
          >
            {rows.map((row) => {
              const level = statusLevel(row);
              const shown = expanded.has(row.txId);
              return [
                <Row
                  key={row.txId}
                  tone={level === 'error' ? 'error' : undefined}
                  onClick={() =>
                    navigate({
                      name: 'detail',
                      traceId: row.traceId,
                      from: 'errors',
                      service: row.service,
                      back: currentQuery(),
                    })
                  }
                >
                  {service === '' ? (
                    <Cell title={row.service} className="max-w-[180px] truncate">
                      {row.service}
                    </Cell>
                  ) : null}
                  <Cell nowrap tone="muted" className="tabular">
                    <a
                      href={routeHref({
                        name: 'detail',
                        traceId: row.traceId,
                        from: 'errors',
                        service: row.service,
                        back: currentQuery(),
                      })}
                      title={formatFull(row.startedAt)}
                      className="-mx-1 block rounded px-1 text-inherit no-underline"
                    >
                      {formatStamp(row.startedAt)}
                    </a>
                  </Cell>
                  <Cell>
                    <Chip tone={level === 'error' ? 'error' : 'fail'}>{statusText(row)}</Chip>
                  </Cell>
                  <Cell>
                    <Chip tone={TX_TYPE_TONE[row.txType]}>{TX_TYPE_LABEL[row.txType]}</Chip>
                  </Cell>
                  <Cell mono title={row.entry} className="max-w-[320px] truncate">
                    {row.entry}
                  </Cell>
                  <Cell mono nowrap className="tabular">
                    {formatDuration(row.durationMs)}
                  </Cell>
                  <Cell className="max-w-[360px]">
                    <span className="flex items-start gap-2">
                      <span className="min-w-0 truncate">{reasonOf(row)}</span>
                      {row.errorMessage !== undefined ? (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggle(row.txId);
                          }}
                          aria-expanded={shown}
                          className="shrink-0 rounded-md px-2 py-0.5 text-link transition-colors duration-150 hover:bg-band"
                        >
                          {shown ? '접기' : '자세히'}
                        </button>
                      ) : null}
                    </span>
                  </Cell>
                  <Cell mono tone="muted" title={row.instance} className="max-w-[200px] truncate">
                    {row.instance ?? '-'}
                  </Cell>
                </Row>,
                shown && row.errorMessage !== undefined ? (
                  <tr key={`${row.txId}-message`} className="border-b border-hair">
                    <td colSpan={COLUMNS.length + (service === '' ? 1 : 0)} className="px-4 pb-3">
                      <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-band p-3 font-mono text-xs leading-relaxed text-ink">
                        {row.errorMessage}
                      </pre>
                      {row.clientAddress !== undefined || row.userAgent !== undefined ? (
                        <p className="mt-1 break-all text-xs text-muted">
                          {row.clientAddress ?? ''}
                          {row.clientAddress !== undefined && row.userAgent !== undefined
                            ? ' · '
                            : ''}
                          {row.userAgent ?? ''}
                        </p>
                      ) : null}
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </Table>
        ) : null}
        {!grouped && data !== undefined ? (
          severity === 'all' ? (
            <PickedNote fetched={data.rows.length} total={data.total} picked={rows.length} />
          ) : (
            <LimitNote shown={rows.length} total={counts.total} />
          )
        ) : null}
      </Section>
    </Page>
  );
}

/**
 * 심각도 "전체"의 안내 (44회차). 그때는 서버에 상태 조건을 걸지 않고(한 단계만 받는다) 최근 몇 건을
 * 받아 화면에서 오류·실패만 남긴다. 서버의 `total`은 정상까지 센 수라 `LimitNote`로 "전체 N건 중
 * 상위 M건"이라 적으면 오류가 N건인 것처럼 읽힌다. 받은 것을 다 봤으면 적을 것이 없다
 */
function PickedNote({
  fetched,
  total,
  picked,
}: {
  fetched: number;
  total: number;
  picked: number;
}) {
  if (total <= fetched) return null;
  return (
    <p className="tabular mt-3 text-xs text-muted">
      <Lines
        lines={[
          `조회한 요청 ${formatCount(total)}건 중 최근 ${formatCount(fetched)}건에서 오류·실패 ${formatCount(picked)}건을 골랐다.`,
          '심각도를 고르면 서버가 오류·실패만 골라 준다',
        ]}
      />
    </p>
  );
}

/** 무엇이 잘못됐는지 한 줄로. 예외 메시지가 있으면 그것이 가장 구체적이다 */
function reasonOf(row: TxSummary): string {
  if (row.errorMessage !== undefined && row.errorMessage !== '') return row.errorMessage;
  if (row.errorType !== undefined) return row.errorType;
  if (row.sqlErrorCount > 0) {
    return `SQL 오류 ${row.sqlErrorCount}건${row.sqlErrorCode ? ` (${row.sqlErrorCode})` : ''}`;
  }
  if (row.httpStatus !== undefined) return `상태 코드 ${row.httpStatus}`;
  return '메시지 없음';
}

/** 주소의 조건 → 화면 상태. 기간은 진단 메뉴가 읽는다 */
function parseQuery(query?: string): { severity: Severity; instance: string; grouped: boolean } {
  const fallback = { severity: 'all' as Severity, instance: '', grouped: true };
  if (query === undefined || query === '') return fallback;
  const params = new URLSearchParams(query);
  const severity = params.get('severity');
  return {
    severity: severity === 'error' || severity === 'fail' ? severity : 'all',
    instance: params.get('instance') ?? '',
    grouped: params.get('grouped') !== '0',
  };
}
